import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { PageWithTitle } from 'src/Common/components/PageWithTitle/PageWithTitle';
import { useVenueAccounts, VenueAccount } from '../api/useVenueAccounts';
import {
  CONSUMER_STATE_LABEL,
  COVERAGE_REASON_LABEL,
  ConsumerHealthState,
  MASKED_IDENTITY,
  ResolvedConsumer,
  VenueAccountRateLimitRow,
  findAccountHealth,
  findAccountRateLimits,
  hasIdentity,
  parseMaxQuotedMarkets,
  resolveConsumers,
  unattributedTotal,
  useAccountRateLimits,
  useAccountsHealth,
  useUpdateVenueAccount
} from '../api/useAccountsAdmin';
import {
  UNAVAILABLE_LABEL,
  failureReasonLabel,
  findPerformanceAccount,
  formatUsdOrUnavailable,
  usePerformance
} from '../api/usePositions';

const DEGRADED_COLOR = '#ff9800';

const STATE_COLOR: Record<ConsumerHealthState, string> = {
  ok: '#4caf50',
  stale: DEGRADED_COLOR,
  'never-reported': '#9e9e9e',
  absent: '#f44336',
  unreachable: '#f44336'
};

const cellSx = { fontSize: '0.78rem', borderColor: 'grey.900' } as const;

const consumerTooltip = ({ consumer, state, entry }: ResolvedConsumer) => {
  if (state === 'absent') {
    return `${consumer}: expected to report and never did — the pod may be down or was never given this account`;
  }
  const parts: string[] = [];
  if (entry?.checkedAt) parts.push(`last check ${entry.checkedAt}`);
  if (entry?.ageMs != null) parts.push(`${Math.round(entry.ageMs / 1000)}s ago`);
  if (entry?.credentialsMounted === false) parts.push('credentials not mounted');
  if (entry?.missingKeys?.length) parts.push(`missing: ${entry.missingKeys.join(', ')}`);
  return `${consumer}: ${CONSUMER_STATE_LABEL[state]}${parts.length ? ` — ${parts.join(' · ')}` : ''}`;
};

interface EditState {
  account: VenueAccount;
  name: string;
  enabled: boolean;
  maxQuotedMarkets: string;
}

export const AccountsPage = () => {
  const accountsQuery = useVenueAccounts();
  const healthQuery = useAccountsHealth();
  const rateLimitsQuery = useAccountRateLimits();
  // `isLoading`/`isError` and not merely `!data`: a read that FAILED leaves
  // `data` undefined forever, and a spinner that never resolves reads as
  // "wait a moment" when the truth is "this will never arrive".
  const {
    data: performance,
    isLoading: performanceLoading,
    isError: performanceError
  } = usePerformance();
  const updateAccount = useUpdateVenueAccount();

  const [edit, setEdit] = useState<EditState | null>(null);

  const accounts = useMemo(
    () =>
      [...(accountsQuery.data ?? [])].sort(
        (a, b) =>
          a.platform.localeCompare(b.platform) || a.name.localeCompare(b.name)
      ),
    [accountsQuery.data]
  );

  const roster = healthQuery.data?.consumers ?? [];
  const unattributed = unattributedTotal(rateLimitsQuery.data);

  const maxParse = edit
    ? parseMaxQuotedMarkets(edit.maxQuotedMarkets)
    : ({ ok: true, value: null } as const);

  const openEdit = (account: VenueAccount) =>
    setEdit({
      account,
      name: account.name,
      enabled: account.enabled,
      maxQuotedMarkets:
        account.maxQuotedMarkets == null ? '' : String(account.maxQuotedMarkets)
    });

  const submitEdit = () => {
    if (!edit || !maxParse.ok) return;
    updateAccount.mutate(
      {
        platform: edit.account.platform,
        accountId: edit.account.accountId,
        patch: {
          name: edit.name,
          enabled: edit.enabled,
          maxQuotedMarkets: maxParse.value
        }
      },
      { onSuccess: () => setEdit(null) }
    );
  };

  const toggleEnabled = (account: VenueAccount) =>
    updateAccount.mutate({
      platform: account.platform,
      accountId: account.accountId,
      patch: { enabled: !account.enabled }
    });

  return (
    <PageWithTitle title="Cuentas">
      <Box width="100%">
        {accountsQuery.isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            El registro de cuentas no respondió. La lista de abajo no es la
            realidad — es lo último que se pudo leer.
          </Alert>
        )}
        {healthQuery.isError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            No se pudo leer la salud de credenciales: ningún consumer puede
            considerarse alcanzable.
          </Alert>
        )}
        {rateLimitsQuery.isError && (
          <Alert severity="warning" sx={{ mb: 1 }} data-rate-limits-error="true">
            No se pudieron leer los contadores de rate limit. Las columnas 429 y
            self-throttled quedan en {UNAVAILABLE_LABEL} — no en cero.
          </Alert>
        )}
        {unattributed != null && unattributed > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }} data-unattributed={unattributed}>
            {`${unattributed} evento(s) contados en la ventana no llevan cuenta: los números por cuenta de abajo son un PISO, no un total. Hay un productor sin estampar (un engine sin KALSHI_ACCOUNT_ID / POLY_ACCOUNT_ID).`}
          </Alert>
        )}
        {rateLimitsQuery.data && unattributed == null && (
          <Alert severity="warning" sx={{ mb: 1 }} data-unattributed="unknown">
            No se pudo saber cuántos eventos de la ventana quedaron sin cuenta:
            la respuesta no trae ese dato. Los números por cuenta de abajo
            pueden ser un PISO — no se los puede leer como un total.
          </Alert>
        )}

        {accountsQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ borderColor: 'grey.900' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx}>Cuenta</TableCell>
                  <TableCell sx={cellSx}>Venue</TableCell>
                  <TableCell sx={cellSx}>accountId</TableCell>
                  <TableCell sx={cellSx}>Habilitada</TableCell>
                  <TableCell sx={cellSx}>Max quoted</TableCell>
                  <TableCell sx={cellSx}>Identidad</TableCell>
                  <TableCell sx={cellSx}>Balance</TableCell>
                  <Tooltip title="429 del VENUE en el camino de órdenes (`order_rejected_rate_limited`): la venue nos rechazó. Lo único que lo alivia es OTRA cuenta. Nunca se suma con self-throttled. Cuenta solo el camino de órdenes del engine — los lectores de posiciones no emiten nada cuando los limitan.">
                    <TableCell sx={cellSx}>429 (venue)</TableCell>
                  </Tooltip>
                  <Tooltip title="Esperas de NUESTRO token bucket (`order_rate_limit_waiting`) para no comerse un 429. Se ajusta en config; NO es la venue rechazándonos, y agregar una cuenta no lo baja.">
                    <TableCell sx={cellSx}>self-throttled</TableCell>
                  </Tooltip>
                  <TableCell sx={cellSx}>Credenciales por consumer</TableCell>
                  <TableCell sx={cellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} sx={cellSx}>
                      <Typography variant="body2" color="text.secondary">
                        {accountsQuery.isError
                          ? 'Sin datos del registro'
                          : 'No hay cuentas registradas'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {accounts.map(account => {
                  const key = `${account.platform}:${account.accountId}`;
                  const health = findAccountHealth(
                    healthQuery.data,
                    account.platform,
                    account.accountId
                  );
                  const consumers = resolveConsumers(roster, health);
                  const perf = findPerformanceAccount(
                    performance,
                    account.platform,
                    account.accountId
                  );
                  const rateLimits = findAccountRateLimits(
                    rateLimitsQuery.data,
                    account.platform,
                    account.accountId
                  );
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={cellSx}>{account.name}</TableCell>
                      <TableCell sx={cellSx}>{account.platform}</TableCell>
                      <TableCell sx={{ ...cellSx, color: 'grey.500' }}>
                        {account.accountId}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Switch
                          size="small"
                          checked={account.enabled}
                          onChange={() => toggleEnabled(account)}
                          inputProps={{
                            'aria-label': `enabled ${key}`
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {account.maxQuotedMarkets == null
                          ? 'sin tope'
                          : account.maxQuotedMarkets}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {hasIdentity(account) ? (
                          <Tooltip title="Credencial configurada. La clave vive solo en el Secret de k8s y nunca se muestra acá.">
                            <span>{MASKED_IDENTITY}</span>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: DEGRADED_COLOR }}
                          >
                            sin credencial
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <BalanceCell
                          account={perf}
                          loading={performanceLoading}
                          errored={performanceError}
                        />
                      </TableCell>
                      <TableCell sx={cellSx} data-rate-limit-cell={`venue429:${key}`}>
                        <RateLimitCell
                          row={rateLimits}
                          count={rateLimits?.venueRejected429}
                          loading={rateLimitsQuery.isLoading}
                          errored={rateLimitsQuery.isError}
                        />
                      </TableCell>
                      <TableCell sx={cellSx} data-rate-limit-cell={`self:${key}`}>
                        <RateLimitCell
                          row={rateLimits}
                          count={rateLimits?.selfThrottled}
                          loading={rateLimitsQuery.isLoading}
                          errored={rateLimitsQuery.isError}
                        />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {consumers.length === 0 ? (
                            <Typography
                              variant="caption"
                              sx={{ color: DEGRADED_COLOR }}
                            >
                              sin datos de salud
                            </Typography>
                          ) : (
                            consumers.map(resolved => (
                              <Tooltip
                                key={resolved.consumer}
                                title={consumerTooltip(resolved)}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${resolved.consumer}: ${CONSUMER_STATE_LABEL[resolved.state]}`}
                                  sx={{
                                    borderRadius: '4px',
                                    fontSize: '0.68rem',
                                    borderColor: STATE_COLOR[resolved.state],
                                    color: STATE_COLOR[resolved.state]
                                  }}
                                />
                              </Tooltip>
                            ))
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Button size="small" onClick={() => openEdit(account)}>
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Dialog open={edit != null} onClose={() => setEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: '1rem' }}>
          {edit ? `${edit.account.platform} · ${edit.account.accountId}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Nombre"
              size="small"
              value={edit?.name ?? ''}
              onChange={e =>
                setEdit(prev => (prev ? { ...prev, name: e.target.value } : prev))
              }
              inputProps={{ 'aria-label': 'account name' }}
            />
            <TextField
              label="Max quoted markets"
              size="small"
              value={edit?.maxQuotedMarkets ?? ''}
              onChange={e =>
                setEdit(prev =>
                  prev ? { ...prev, maxQuotedMarkets: e.target.value } : prev
                )
              }
              error={!maxParse.ok}
              helperText={
                maxParse.ok
                  ? 'Vacío = sin tope. Mínimo 1.'
                  : maxParse.error
              }
              inputProps={{ 'aria-label': 'max quoted markets' }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={edit?.enabled ?? false}
                  onChange={(_, checked) =>
                    setEdit(prev => (prev ? { ...prev, enabled: checked } : prev))
                  }
                  inputProps={{ 'aria-label': 'account enabled' }}
                />
              }
              label="Habilitada"
            />
            <Typography variant="caption" color="text.secondary">
              Las credenciales no se cargan desde esta pantalla: viven en el
              Secret de k8s que la cuenta nombra.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setEdit(null)}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!maxParse.ok || updateAccount.isLoading}
            onClick={submitEdit}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </PageWithTitle>
  );
};

/**
 * The live balance for one account. A read that is not `ok` shows the
 * unavailable marker with its reason — never a zero, which would be
 * indistinguishable from an empty account.
 */
const BalanceCell = ({
  account,
  loading,
  errored
}: {
  account: ReturnType<typeof findPerformanceAccount>;
  loading: boolean;
  /** The read FAILED. `…` would promise an arrival that is not coming. */
  errored: boolean;
}) => {
  if (errored) {
    return (
      <Typography variant="caption" sx={{ color: DEGRADED_COLOR }}>
        {`${UNAVAILABLE_LABEL} — la lectura de performance falló`}
      </Typography>
    );
  }
  if (loading) return <span>…</span>;
  if (!account) {
    return (
      <Typography variant="caption" sx={{ color: DEGRADED_COLOR }}>
        {`${UNAVAILABLE_LABEL} — sin reporte`}
      </Typography>
    );
  }
  if (!account.ok || account.totalUsd == null) {
    return (
      <Tooltip title={account.detail ?? failureReasonLabel(account.reason)}>
        <Typography variant="caption" sx={{ color: DEGRADED_COLOR }}>
          {`${UNAVAILABLE_LABEL} — ${failureReasonLabel(account.reason)}`}
        </Typography>
      </Tooltip>
    );
  }
  return <span>{formatUsdOrUnavailable(account.totalUsd)}</span>;
};

const fmtCoverageSince = (since: string | null | undefined): string => {
  if (!since) return '';
  const at = new Date(since);
  return isNaN(at.getTime()) ? since : at.toLocaleString();
};

const coverageReasonText = (
  reason: VenueAccountRateLimitRow['coverage']['reason'] | undefined
): string =>
  reason != null &&
  Object.prototype.hasOwnProperty.call(COVERAGE_REASON_LABEL, reason)
    ? COVERAGE_REASON_LABEL[reason]
    : 'cobertura desconocida — no se puede afirmar nada sobre este número';

/**
 * One rate-limit counter. A `null` count means the window is NOT covered, and
 * it renders as the unknown marker naming the date the data starts — never as
 * `0`. On a fresh deploy every account legitimately has nothing stamped, and a
 * zero there is a false all-clear indistinguishable from "the venue is not
 * refusing us".
 */
const RateLimitCell = ({
  row,
  count,
  loading,
  errored
}: {
  row: VenueAccountRateLimitRow | undefined;
  count: number | null | undefined;
  loading: boolean;
  errored: boolean;
}) => {
  if (errored) {
    return (
      <Typography variant="caption" data-rate-limit="errored" sx={{ color: DEGRADED_COLOR }}>
        {UNAVAILABLE_LABEL}
      </Typography>
    );
  }
  if (loading) return <span data-rate-limit="loading">…</span>;
  if (!row) {
    return (
      <Typography variant="caption" data-rate-limit="no-row" sx={{ color: DEGRADED_COLOR }}>
        {`${UNAVAILABLE_LABEL} — sin reporte`}
      </Typography>
    );
  }
  const since = fmtCoverageSince(row.coverage?.since);
  if (count == null) {
    return (
      <Tooltip title={coverageReasonText(row.coverage?.reason)}>
        <Typography
          variant="caption"
          data-rate-limit="uncovered"
          sx={{ color: DEGRADED_COLOR }}
        >
          {since
            ? `— (sin datos antes de ${since})`
            : '— (sin datos: nada estampado todavía)'}
        </Typography>
      </Tooltip>
    );
  }
  // A number under an uncovered window is a FLOOR, not a total, and says so.
  const covered = row.coverage?.covered === true;
  return (
    <Tooltip
      title={`${coverageReasonText(row.coverage?.reason)}${
        since ? ` · datos desde ${since}` : ''
      }`}
    >
      <Typography
        variant="caption"
        data-rate-limit={covered ? 'counted' : 'partial'}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          color: covered ? undefined : DEGRADED_COLOR
        }}
      >
        {covered ? String(count) : `≥ ${count} (parcial)`}
      </Typography>
    </Tooltip>
  );
};
